import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput, LabeledStatus } from 'components/molecules';
import LabeledProductType from 'components/molecules/labeledStatus/LabeledProductType';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible, useHasPermission } from 'hooks';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import { PiMusicNotesPlus, PiQuestion, PiWarning } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetMusicProductsDigitalList } from 'services/music-product-digital';
import {
  DIGITAL_PRODUCT_TYPE,
  RELEASE_TYPE,
} from 'services/music-product-digital/types/enums';
import {
  IMusicProductDigitalResponse,
  IMusicProductDigitalSearch,
} from 'services/music-product-digital/types/query';
import { STATUS } from 'services/types';
import { useSearchById, useSetSearchById } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { formatDate } from 'utils/global/date.utils';
import { getNodes } from 'utils/table/nodes.utils';

enum MUSIC_PRODUCT_DIGITAL_COLUMNS {
  productType,
  title,
  release,
  ean,
  // externalIds,
  copyrights,
  releaseStatus,
}

const musicProductDigitalSearchInitialValues: IMusicProductDigitalSearch = {
  ean: '',
  artistName: '',
  artistNickname: '',
  isrc: '',
  title: '',
  status: undefined,
  productType: undefined,
  showOnlyWithoutContracts: undefined,
  deliveryDiscrepancy: undefined,
};

const MusicProductsDigital = () => {
  usePermissionGuard('DigitalMusicProductPermissionsLIST**', '/', true, 'music-product-digital');

  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasPermission = useHasPermission();

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const columnsVisibility = useTableColumnsVisibility(
    'MusicProductsDigital5',
    MUSIC_PRODUCT_DIGITAL_COLUMNS
  );

  const i18nextPrefix = 'music-product-digital.list';

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters =
    searchById[columnsVisibility.tableId] ||
    musicProductDigitalSearchInitialValues;

  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useGetMusicProductsDigitalList({
      ...filters,
      showOnlyWithoutContracts: filters.showOnlyWithoutContracts
        ? filters.showOnlyWithoutContracts
        : undefined,
      deliveryDiscrepancy: filters.deliveryDiscrepancy
        ? filters.deliveryDiscrepancy
        : undefined,
    });

  const tableData = getNodes<IMusicProductDigitalResponse>(data?.pages.flat());

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }
    
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/contracts`, {
      relative: 'path',
    });
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const hasDetailPermission = hasPermission('DigitalMusicProductPermissionsLIST**', 16);
  const tableSelect = hasDetailPermission ? select : undefined;

  const { getSelectOptions } = useEnumToSelectOptions();

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title`)}
          path=''
          hideBackButton
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={MUSIC_PRODUCT_DIGITAL_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix={i18nextPrefix}
                />
              </div>
              {hasPermission('DigitalMusicProductPermissionsLIST**', 1) && (
                <Button
                  variant={BUTTON_VARIANT.submit}
                  onClick={() => navigate('create/tracks', { replace: false })}>
                  <Button.Text
                    text={t('button.create-what', {
                      what: t(`${i18nextPrefix}.what`),
                    })}
                  />
                  <Button.Icon icon={<PiMusicNotesPlus />} />
                </Button>
              )}
            </>
          }>
          <QueryFilter<IMusicProductDigitalSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IMusicProductDigitalSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values =>
                  setSearchById(columnsVisibility.tableId, values)
                }>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='productType'
                    label={t('input.label.productType')}
                    options={getSelectOptions(
                      DIGITAL_PRODUCT_TYPE,
                      'enum.DIGITAL_PRODUCT_TYPE'
                    )}
                  />
                  <FormInput.Select
                    name='status'
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                  />
                  <FormInput.Text name='title' label={t('input.label.title')} />
                  <FormInput.Text
                    name='artistNickname'
                    label={t('input.label.artistNickname')}
                  />
                  <FormInput.Text
                    name='artistName'
                    label={t('input.label.artistName')}
                  />
                  <FormInput.Text name='ean' label={t('input.label.ean')} />
                  <FormInput.Text name='isrc' label='ISRC' />
                  <FormInput.Switch
                    name='showOnlyWithoutContracts'
                    label={t('input.label.withoutContracts')}
                  />

                  <FormInput.Switch
                    name='deliveryDiscrepancy'
                    label={t('input.label.deliveryDiscrepancy')}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  data.key === 'status'
                    ? t(`enum.STATUS.${STATUS[data.value as STATUS]}`)
                    : data.key === 'productType'
                    ? t(
                        `enum.DIGITAL_PRODUCT_TYPE.${
                          DIGITAL_PRODUCT_TYPE[
                            data.value as DIGITAL_PRODUCT_TYPE
                          ]
                        }`
                      )
                    : data.key === 'showOnlyWithoutContracts'
                    ? data.value
                      ? t('input.label.withoutContracts')
                      : undefined
                    : data.key === 'deliveryDiscrepancy'
                    ? data.value
                      ? t('input.label.deliveryDiscrepancy')
                      : undefined
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={tableSelect}
        columns={[
          {
            label: `${t(`${i18nextPrefix}.table.productType`)}`,
            renderCell: ({ productType }) => (
              <TableItem>
                <LabeledProductType status={productType} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              MUSIC_PRODUCT_DIGITAL_COLUMNS.productType
            ),
          },
          {
            label: `${t(`${i18nextPrefix}.table.title`)}`,
            renderCell: ({ productImagePreviewLink, mainTitle, id }) => (
              <TableItem>
                <div className='flex flex-row items-center gap-4'>
                  <TableItem.Image
                    url={productImagePreviewLink}
                    alt={mainTitle.title}
                  />
                  <div className='flex flex-col'>
                    <TableItem.Text text={mainTitle.title} />
                    {mainTitle?.version && (
                      <TableItem.Text
                        text={mainTitle?.version}
                        isSupplementary
                      />
                    )}
                  </div>
                </div>
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MUSIC_PRODUCT_DIGITAL_COLUMNS.title),
          },
          {
            label: `${t(`${i18nextPrefix}.table.release`)}`,
            renderCell: ({ releaseType, releaseDate }) => (
              <TableItem>
                <TableItem.Text
                  text={
                    releaseType
                      ? t(`enum.RELEASE_TYPE.${RELEASE_TYPE[releaseType]}`)
                      : undefined
                  }
                />
                <TableItem.Text
                  text={releaseDate ? formatDate(releaseDate) : undefined}
                  isSupplementary
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MUSIC_PRODUCT_DIGITAL_COLUMNS.release),
          },
          {
            label: `${t(`${i18nextPrefix}.table.ean`)}`,
            renderCell: ({ ean }) => (
              <TableItem>
                <TableItem.Text text={ean ?? undefined} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MUSIC_PRODUCT_DIGITAL_COLUMNS.ean),
          },
          {
            label: t(`${i18nextPrefix}.table.copyrights`),
            renderCell: ({ cLine, pLine }) => (
              <TableItem>
                <TableItem.Text
                  isSupplementary
                  text={
                    cLine?.name && cLine?.year
                      ? `© ${cLine.year} ${cLine.name}`
                      : undefined
                  }
                />
                <TableItem.Text
                  isSupplementary
                  text={
                    pLine?.name && pLine?.year
                      ? `℗ ${pLine.year} ${pLine.name}`
                      : undefined
                  }
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              MUSIC_PRODUCT_DIGITAL_COLUMNS.copyrights
            ),
          },
          {
            label: t(`${i18nextPrefix}.table.releaseStatus`),
            renderCell: ({ releaseStatus, mainTitle, id }) => (
              <TableItem justifyPosition='center'>
                <LabeledStatus status={releaseStatus} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              MUSIC_PRODUCT_DIGITAL_COLUMNS.releaseStatus
            ),
          },
          {
            label: '',
            renderCell: ({
              hasDeliveryDiscrepancy,
              deliveryHasErrors,
              mainTitle,
              id,
            }) => (
              <TableItem justifyPosition='end'>
                <div className='flex '>
                  {hasDeliveryDiscrepancy && (
                    <Button
                      onlyIcon
                      isClear
                      variant={BUTTON_VARIANT.warning}
                      onClick={() => navigate(`${id}/delivery`)}>
                      <Button.Icon icon={<PiQuestion />} />
                    </Button>
                  )}
                  {deliveryHasErrors && (
                    <Button
                      onlyIcon
                      isClear
                      variant={BUTTON_VARIANT.remove}
                      onClick={() => navigate(`${id}/delivery`)}>
                      <Button.Icon icon={<PiWarning />} />
                    </Button>
                  )}
                </div>
              </TableItem>
            ),
          },
        ]}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        data={tableData}
        displayMode
      />
    </TopbarPage>
  );
};

export default MusicProductsDigital;
